// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/readiness/MvpGateCard.jsx

import React from "react";

function gateLabel(status) {
  if (status === "passed") return "Ready";
  if (status === "warning") return "Conditionally ready";
  if (status === "blocked") return "Blocked";
  return "Not evaluated";
}

function statusPillLabel(status) {
  if (status === "passed") return "Ready";
  if (status === "warning") return "Warnings open";
  if (status === "blocked") return "Blocked";
  return "Pending";
}

function refreshLabel(value) {
  if (!value) return "Latest readiness state loaded.";

  try {
    const refreshed = new Date(value);
    return `Refreshed ${refreshed.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  } catch {
    return "Refreshed just now";
  }
}

export default function MvpGateCard({
  gate,
  score = 0,
  blockers = [],
  warnings = [],
  onRefresh,
  saving,
  refreshedAt,
}) {
  const status = gate?.status || "unknown";
  const label = gateLabel(status);
  const blockerCount = gate?.critical_blockers ?? blockers.length;
  const warningCount = gate?.warnings ?? warnings.length;
  const passedCount = gate?.passed_checks ?? 0;
  const totalChecks = gate?.total_checks ?? 0;

  return (
    <div className={`enterprise-admin-mvp-gate-card status-${status} ${saving ? "is-refreshing" : ""}`}>
      <div className="enterprise-admin-mvp-gate-primary">
        <span className={`enterprise-admin-mvp-status-pill status-${status}`}>
          {statusPillLabel(status)}
        </span>
        <h4>{label}</h4>
        <p>
          {gate?.launch_recommendation ||
            "Run readiness to evaluate whether this organization is ready for operational rollout."}
        </p>
      </div>

      <div className="enterprise-admin-mvp-score">
        <span className="enterprise-admin-mvp-score-label">Readiness score</span>
        <strong>{score}%</strong>
        <small>
          {passedCount} of {totalChecks} checks passed
        </small>
      </div>

      <div className="enterprise-admin-mvp-gate-stats">
        <article>
          <span>Blockers</span>
          <strong>{blockerCount}</strong>
        </article>
        <article>
          <span>Warnings</span>
          <strong>{warningCount}</strong>
        </article>
      </div>

      <div className="enterprise-admin-readiness-refresh-panel">
        <button
          type="button"
          className="enterprise-admin-mvp-gate-refresh"
          onClick={onRefresh}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="enterprise-admin-refresh-spinner" aria-hidden="true" />
              Refreshing…
            </>
          ) : (
            "Refresh readiness"
          )}
        </button>
        <small>{saving ? "Checking readiness…" : refreshLabel(refreshedAt)}</small>
      </div>
    </div>
  );
}
